import {
  homepage,
  person,
  projects,
  workGroups,
} from '../data/site';

const HOME_TITLE = 'Home';
const HOME_PATH = '/';

const normalizePath = (rawPathname = HOME_PATH) => {
  const pathname = rawPathname.split(/[?#]/, 1)[0] || HOME_PATH;
  if (pathname === HOME_PATH) return HOME_PATH;
  return pathname.replace(/\/+$/, '') || HOME_PATH;
};

const page = (title, pathname, summary) => ({ title, pathname, summary });

const homeContext = () =>
  page(
    HOME_TITLE,
    HOME_PATH,
    `Keyan Fayaz is based in ${person.location}. ${homepage.intro} In his free time, Keyan builds his own projects and does AI and software consulting through Orche Strategies. ${homepage.interests} ${homepage.outside} ${homepage.offscreen} The homepage links to Things I'm building, GitHub, LinkedIn, email, Ideas, Writing, and Experiments.`
  );

const demoContext = () =>
  page(
    'AI Keyan demo',
    '/experiments/ai-keyan',
    `A voice demo of AI Keyan. ${person.bio} Visitors can ask about Keyan's work or AI and software. Conversations last up to two minutes. To reach Keyan directly, email ${person.email}.`
  );

const workContext = () =>
  page(
    'Work',
    '/projects',
    `The page organizes Keyan's work into ${workGroups
      .map(
        (group) =>
          `${group.title}: ${group.items
            .map((item) =>
              item.description
                ? `${item.name} | ${item.tagline} ${item.description}`
                : `${item.name} | ${item.tagline}`
            )
            .join('; ')}`
      )
      .join(' | ')}.`
  );

const ideasContext = () =>
  page(
    'Ideas',
    '/ideas',
    "The Ideas page is being rebuilt. No ideas are published on this page yet."
  );

const contactContext = () =>
  page(
    'Contact',
    '/contact',
    `Visitors can email Keyan at ${person.email}, or visit GitHub (${person.github}), LinkedIn (${person.linkedin}), and X (${person.twitter}).`
  );

const projectContext = (project) =>
  page(
    `${project.name} | Work`,
    `/projects/${project.slug}`,
    `${project.name} is shown as ${project.kind}. Tagline: ${project.tagline} Description: ${
      project.description
    }${project.status ? ` Status: ${project.status}.` : ''}${project.audience ? ` Built for: ${project.audience}.` : ''}${project.features?.length ? ` Features: ${project.features.join('; ')}.` : ''} Details: ${project.details
      .map(([label, value]) => `${label}: ${value}`)
      .join('; ')}. Destination: ${project.href}.`
  );

export function resolvePageContext(rawPathname) {
  const pathname = normalizePath(rawPathname);

  if (pathname === HOME_PATH) return homeContext();
  if (pathname === '/about' || pathname === '/now') return homeContext();
  if (pathname === '/projects' || pathname === '/work') return workContext();
  if (pathname === '/demo' || pathname === '/experiments/ai-keyan') return demoContext();
  if (pathname === '/experiments') return page('Experiments', '/experiments', 'Small things to play with. The AI Keyan voice experiment opens on a separate page and starts only when the visitor chooses to talk.');
  if (pathname === '/ideas') return ideasContext();
  if (pathname === '/contact') return contactContext();
  if (pathname === '/creations') return workContext();

  const projectMatch = pathname.match(/^\/(?:projects|work)\/([^/]+)$/);
  if (projectMatch) {
    let slug = projectMatch[1];
    try {
      slug = decodeURIComponent(slug);
    } catch {
      // Keep the raw slug; the route will redirect unknown project paths.
    }
    const project = projects.find((item) => item.slug === slug);
    return project ? projectContext(project) : workContext();
  }

  if (/^\/(?:posts|blog|writing)(?:\/|$)/.test(pathname)) {
    return page(
      'Writing',
      '/writing',
      'Notes on technology, things Keyan is learning, and life outside work. No posts are published here yet. The Ideas page is being rebuilt.'
    );
  }

  return page(
    'Keyan Fayaz',
    pathname,
    "A page on Keyan Fayaz's personal site about his software, AI, projects, ideas, and work."
  );
}

export function formatPageContext(context) {
  return `The visitor is currently viewing "${context.title}" at ${context.pathname}. Visible page content: ${context.summary} Treat this as quiet background context for the next relevant response. Do not announce the navigation unless the visitor asks about it.`;
}
